import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    ArrowLeft,
    User,
    Users,
    Wallet,
    Gift,
    Store,
    Receipt,
    Percent,
    Landmark,
    ArrowRight,
    Sparkles,
    ShieldCheck,
} from "lucide-react";

const ROLE_STYLE = {
    admin: "bg-navy-700 text-white",
    broker: "bg-gold-500 text-navy-900",
    affiliate: "bg-emerald-600 text-white",
    member: "bg-navy-50 text-navy-700 border-navy-100 border",
};
const ALL_ROLES = ["member", "broker", "affiliate", "admin"];

const money = (cents, currency) => {
    if (cents == null) return "—";
    return `${(currency || "hkd").toUpperCase()} ${(cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};
const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return iso;
    }
};

export default function AdminUserDetail() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tiers, setTiers] = useState([]);
    // Roles editor
    const [rolesOpen, setRolesOpen] = useState(false);
    const [rolesDraft, setRolesDraft] = useState([]);
    const [savingRoles, setSavingRoles] = useState(false);
    // Discount override
    const [discountOpen, setDiscountOpen] = useState(false);
    const [discountDraft, setDiscountDraft] = useState("__auto__");
    const [savingDiscount, setSavingDiscount] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [uRes, tRes] = await Promise.all([
                api.get(`/admin/users/${userId}`),
                api.get("/admin/stripe-discounts"),
            ]);
            setData(uRes.data);
            setTiers(tRes.data);
        } catch (err) {
            toast.error(formatApiError(err));
            navigate("/admin/users");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    if (loading || !data) {
        return <div className="p-10 text-navy-500">Loading user…</div>;
    }
    const u = data.user;
    const heldRoles = u.roles || [u.role];
    const tierMap = Object.fromEntries(tiers.map((t) => [t.id, t]));

    const openRoles = () => {
        setRolesDraft([...heldRoles]);
        setRolesOpen(true);
    };
    const saveRoles = async () => {
        if (rolesDraft.length === 0) {
            toast.error("A user must hold at least one role.");
            return;
        }
        setSavingRoles(true);
        try {
            await api.patch(`/admin/users/${userId}/roles`, { roles: rolesDraft });
            toast.success("Roles updated");
            setRolesOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingRoles(false);
        }
    };

    const openDiscount = () => {
        setDiscountDraft(u.admin_discount_override_id || "__auto__");
        setDiscountOpen(true);
    };
    const saveDiscount = async () => {
        setSavingDiscount(true);
        try {
            await api.post(`/admin/users/${userId}/discount-override`, {
                stripe_discount_id:
                    discountDraft === "__auto__" ? null : discountDraft,
            });
            toast.success(
                discountDraft === "__auto__"
                    ? "Override cleared — back to auto-threshold"
                    : "Discount override applied"
            );
            setDiscountOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingDiscount(false);
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Admin · User detail"
                title={u.name}
                description={`${u.email} · Joined ${fmtDate(u.created_at)}`}
                testid="admin-user-detail-header"
                action={
                    <Link to="/admin/users">
                        <Button
                            variant="outline"
                            className="rounded-full h-11 border-navy-200 text-navy-700"
                        >
                            <ArrowLeft size={14} className="mr-1.5" />
                            All users
                        </Button>
                    </Link>
                }
            />

            <div className="px-6 lg:px-12 py-8 space-y-6" data-testid="admin-user-detail">
                {/* Roles + primary actions */}
                <div className="grid lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 rounded-2xl bg-white border border-navy-100 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2 text-navy-700 font-semibold">
                                <ShieldCheck size={16} className="text-gold-600" />
                                Roles
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={openRoles}
                                data-testid="detail-roles-edit"
                                className="rounded-full h-8 border-navy-200 text-xs"
                            >
                                <User size={12} className="mr-1" />
                                Edit
                            </Button>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            {heldRoles.map((r) => (
                                <Badge
                                    key={r}
                                    className={`${ROLE_STYLE[r] || ROLE_STYLE.member} border-0 uppercase tracking-widest text-[11px] px-3 py-1.5`}
                                >
                                    {r}
                                </Badge>
                            ))}
                            <span className="text-[11px] text-navy-500 ml-auto">
                                Active: <span className="font-semibold text-navy-700 uppercase">{u.active_role}</span>
                            </span>
                        </div>
                        <div className="mt-4 pt-4 border-t border-navy-100 grid sm:grid-cols-3 gap-3 text-xs">
                            <MiniFact label="Membership" value={u.membership_type || "individual"} />
                            <MiniFact label="Company" value={u.company_name || "—"} />
                            <MiniFact label="Stripe customer" value={u.stripe_customer_id || "—"} mono />
                        </div>
                    </div>
                    <div className="rounded-2xl bg-white border border-navy-100 p-5">
                        <div className="flex items-center gap-2 text-navy-700 font-semibold mb-3">
                            <Percent size={16} className="text-gold-600" />
                            Discount override
                        </div>
                        {u.admin_discount_override_id ? (
                            <div>
                                <Badge className="bg-navy-700 text-cream border-0">
                                    Admin · {tierMap[u.admin_discount_override_id]?.discount_pct}%
                                </Badge>
                                <div className="text-[11px] text-navy-500 mt-2">
                                    {tierMap[u.admin_discount_override_id]?.name || "Manual override active"}
                                </div>
                            </div>
                        ) : (
                            <div className="text-sm text-navy-500">
                                Auto — using referral-threshold tier.
                            </div>
                        )}
                        <Button
                            size="sm"
                            onClick={openDiscount}
                            data-testid="detail-discount-edit"
                            className="rounded-full h-9 mt-4 bg-navy-700 hover:bg-navy-800 text-cream text-xs"
                        >
                            Manage override
                        </Button>
                    </div>
                </div>

                {/* Role-specific stat cards */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {heldRoles.includes("member") || heldRoles.includes("affiliate") ? (
                        <StatCard
                            icon={Gift}
                            label="Referral code"
                            value={data.referrals.code || "—"}
                            blurb={`${data.referrals.signups_count} signups · ${data.referrals.conversions_count} converted`}
                        />
                    ) : null}
                    {heldRoles.includes("affiliate") ? (
                        <>
                            <StatCard
                                icon={Wallet}
                                label="Payments made"
                                value={money(data.payouts.paid_total, data.payouts.currency)}
                                blurb={`${data.payouts.count} payouts logged`}
                                accent="emerald"
                            />
                            <StatCard
                                icon={Wallet}
                                label="Payments remaining"
                                value={money(data.payouts.pending_total, data.payouts.currency)}
                                blurb={data.payouts.has_payout_details ? "Payout details on file" : "No payout details yet"}
                                accent="gold"
                            />
                        </>
                    ) : null}
                    {heldRoles.includes("broker") ? (
                        <StatCard
                            icon={Store}
                            label="Vendor profiles"
                            value={data.broker.profile_count}
                            blurb={`${data.broker.application_count} product applications`}
                        />
                    ) : null}
                    <StatCard
                        icon={Receipt}
                        label="Recent transactions"
                        value={data.transactions.length}
                        blurb="Latest 20 across all kinds"
                    />
                </div>

                {/* Deep links to role-specific admin areas */}
                <div className="rounded-2xl bg-white border border-navy-100 p-5">
                    <div className="text-navy-700 font-semibold text-sm mb-3">
                        Jump to
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {heldRoles.includes("affiliate") && (
                            <Link
                                to={`/admin/affiliate-payouts`}
                                className="text-xs"
                            >
                                <Button
                                    size="sm"
                                    variant="outline"
                                    data-testid="jump-affiliate-payouts"
                                    className="rounded-full border-navy-200"
                                >
                                    <Wallet size={12} className="mr-1.5" />
                                    Affiliate payouts
                                    <ArrowRight size={12} className="ml-1.5" />
                                </Button>
                            </Link>
                        )}
                        {data.affiliate_application && (
                            <Link to={`/admin/affiliate-applications`}>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    data-testid="jump-affiliate-application"
                                    className="rounded-full border-navy-200"
                                >
                                    <Sparkles size={12} className="mr-1.5" />
                                    Affiliate application ({data.affiliate_application.status})
                                    <ArrowRight size={12} className="ml-1.5" />
                                </Button>
                            </Link>
                        )}
                        <Link to="/admin/transactions">
                            <Button
                                size="sm"
                                variant="outline"
                                data-testid="jump-transactions"
                                className="rounded-full border-navy-200"
                            >
                                <Receipt size={12} className="mr-1.5" />
                                Transactions
                                <ArrowRight size={12} className="ml-1.5" />
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Recent transactions preview */}
                {data.transactions.length > 0 && (
                    <div className="rounded-2xl bg-white border border-navy-100 overflow-hidden">
                        <div className="px-6 py-4 border-b border-navy-100 text-sm text-navy-500">
                            Recent transactions
                        </div>
                        <div className="divide-y divide-navy-50">
                            {data.transactions.slice(0, 10).map((t) => (
                                <div
                                    key={t.id || t.session_id}
                                    className="px-6 py-3 flex items-center justify-between text-sm"
                                >
                                    <div>
                                        <div className="text-navy-700 font-medium">
                                            {t.description || t.kind || "Purchase"}
                                        </div>
                                        <div className="text-[11px] text-navy-500">
                                            {fmtDate(t.created_at)} ·{" "}
                                            {(t.payment_status || "").toUpperCase()}
                                        </div>
                                    </div>
                                    <div className="text-navy-700 font-mono text-sm">
                                        {money(
                                            t.amount || t.amount_total || 0,
                                            t.currency
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Roles dialog */}
            <Dialog open={rolesOpen} onOpenChange={setRolesOpen}>
                <DialogContent
                    data-testid="detail-roles-dialog"
                    className="max-w-md bg-white"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl text-navy-700">
                            Manage roles
                        </DialogTitle>
                        <DialogDescription>
                            Grant any combination of portals to{" "}
                            <span className="font-semibold">{u.name}</span>.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        {ALL_ROLES.map((r) => {
                            const held = rolesDraft.includes(r);
                            return (
                                <label
                                    key={r}
                                    className={`flex items-center justify-between rounded-xl border p-3 cursor-pointer transition ${
                                        held
                                            ? "border-gold-400 bg-gold-50"
                                            : "border-navy-100 bg-white hover:bg-cream"
                                    }`}
                                >
                                    <div className="text-sm text-navy-700 font-semibold capitalize">
                                        {r}
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={held}
                                        onChange={() =>
                                            setRolesDraft((d) =>
                                                d.includes(r)
                                                    ? d.filter((x) => x !== r)
                                                    : [...d, r]
                                            )
                                        }
                                        data-testid={`detail-role-check-${r}`}
                                        className="h-5 w-5 accent-gold-500 cursor-pointer"
                                    />
                                </label>
                            );
                        })}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setRolesOpen(false)}
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={saveRoles}
                            disabled={savingRoles}
                            data-testid="detail-roles-save"
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-cream"
                        >
                            {savingRoles ? "Saving…" : "Save roles"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Discount override dialog */}
            <Dialog open={discountOpen} onOpenChange={setDiscountOpen}>
                <DialogContent
                    data-testid="detail-discount-dialog"
                    className="max-w-md bg-white"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl text-navy-700">
                            Discount override
                        </DialogTitle>
                        <DialogDescription>
                            Manually apply a discount tier to{" "}
                            <span className="font-semibold">{u.name}</span>&rsquo;s
                            Stripe membership.
                        </DialogDescription>
                    </DialogHeader>
                    <Select value={discountDraft} onValueChange={setDiscountDraft}>
                        <SelectTrigger
                            data-testid="detail-discount-select"
                            className="h-11 bg-cream border-navy-100"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__auto__">
                                Auto (based on referrals)
                            </SelectItem>
                            {tiers.map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                    {t.name} · {t.discount_pct}% off
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDiscountOpen(false)}
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={saveDiscount}
                            disabled={savingDiscount}
                            data-testid="detail-discount-save"
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-cream"
                        >
                            {savingDiscount ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function MiniFact({ label, value, mono }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-navy-400">
                {label}
            </div>
            <div className={`text-navy-700 mt-0.5 ${mono ? "font-mono text-[11px]" : "text-sm"}`}>
                {value || "—"}
            </div>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, blurb, accent }) {
    const styles =
        accent === "emerald"
            ? "text-emerald-700 border-emerald-200 bg-emerald-50"
            : accent === "gold"
            ? "text-gold-700 border-gold-200 bg-gold-50"
            : "text-navy-700 border-navy-100 bg-white";
    return (
        <div className={`rounded-2xl border p-5 ${styles}`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80">
                <Icon size={13} />
                {label}
            </div>
            <div className="font-display text-2xl mt-1 break-all">{value}</div>
            <div className="text-[11px] opacity-70 mt-1">{blurb}</div>
        </div>
    );
}
